<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- En-tête -->
      <div class="mb-6 flex items-center gap-3">
        <div class="p-2 bg-[#22282F]/10 rounded-lg">
          <KeyRound class="w-7 h-7 text-[#22282F]" />
        </div>
        <div class="flex-1">
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Rôles et droits</h1>
          <p class="text-sm text-gray-500">
            Créez un rôle et réglez ce qu'il voit et ce qu'il peut faire, sans toucher au code.
          </p>
        </div>
        <button v-if="canEdit" @click="ouvrirCreation"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 flex items-center gap-2">
          <Plus class="w-4 h-4" /> Nouveau rôle
        </button>
      </div>

      <p v-if="!canEdit && !chargement" class="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
        Lecture seule : seul l'administrateur global peut modifier les rôles et leurs droits.
      </p>
      <div v-if="chargement" class="py-16 text-center text-gray-400">Chargement…</div>
      <div v-else-if="erreurChargement" class="py-16 text-center text-red-500">{{ erreurChargement }}</div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-6 items-start">
        <!-- ============ LISTE DES RÔLES ============ -->
        <aside class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="p-3 border-b border-gray-100">
            <input v-model="recherche" type="search" placeholder="Rechercher un rôle…"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <ul class="max-h-[70vh] overflow-y-auto divide-y divide-gray-100">
            <li v-for="r in rolesFiltres" :key="r.id">
              <button @click="choisir(r.id)"
                :class="['w-full text-left px-4 py-3 hover:bg-gray-50',
                  r.id === selectionId ? 'bg-[#22282F]/5 border-l-4 border-[#22282F]' : 'border-l-4 border-transparent']">
                <div class="flex items-center justify-between gap-2">
                  <span class="font-semibold text-sm text-gray-900 truncate">{{ r.nom }}</span>
                  <span class="text-xs text-gray-400 shrink-0">{{ r.nb_utilisateurs }} util.</span>
                </div>
                <div class="text-xs text-gray-500 font-mono truncate">{{ r.code }}</div>
                <div class="text-xs text-gray-400 mt-0.5">
                  rang {{ r.rang_circuit ?? '—' }} · {{ r.nb_capacites }} capacité(s) · {{ r.nb_etapes_circuit }} étape(s)
                </div>
              </button>
            </li>
            <li v-if="rolesFiltres.length === 0" class="px-4 py-8 text-center text-sm text-gray-400">Aucun rôle.</li>
          </ul>
        </aside>

        <!-- ============ ÉDITEUR ============ -->
        <section v-if="brouillon" class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 pt-4 flex flex-wrap items-center gap-3 border-b border-gray-100">
            <div class="flex-1 min-w-0 pb-3">
              <h2 class="text-lg font-bold text-gray-900 truncate">{{ brouillon.nom || '(sans nom)' }}</h2>
              <span class="text-xs font-mono text-gray-500">{{ brouillon.code }}</span>
            </div>
            <div class="flex gap-1">
              <button v-for="o in onglets" :key="o.id" @click="onglet = o.id"
                :class="['px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
                  onglet === o.id ? 'border-[#22282F] text-[#22282F]' : 'border-transparent text-gray-500 hover:text-gray-800']">
                {{ o.label }}
              </button>
            </div>
          </div>

          <div class="p-5">
            <!-- Général -->
            <div v-if="onglet === 'general'" class="space-y-5 max-w-2xl">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="sm:col-span-2">
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Nom affiché</label>
                  <input v-model="brouillon.nom" :disabled="!canEdit" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Niveau hiérarchique</label>
                  <input v-model.number="brouillon.niveau_hierarchique" type="number" :disabled="!canEdit"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                  <p class="text-xs text-gray-400 mt-1">1 = sommet. Sert à trier et à l'affichage.</p>
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Rang dans les circuits</label>
                  <input v-model.number="brouillon.rang_circuit" type="number" :disabled="!canEdit" placeholder="(aucun)"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                  <p class="text-xs text-gray-400 mt-1">
                    Plus grand = plus haut. Agent 0, Chef de Service 20, Directeur 40 : entre Agent et Chef de Service, prenez 15.
                  </p>
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                  <textarea v-model="brouillon.description" rows="2" :disabled="!canEdit"
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50"></textarea>
                </div>
              </div>
              <div>
                <h3 class="text-sm font-semibold text-gray-700 mb-2">Droits particuliers</h3>
                <label v-for="d in catalogue.drapeaux" :key="d.key" class="flex items-start gap-3 py-2 cursor-pointer">
                  <input type="checkbox" v-model="brouillon.drapeaux[d.key]" :disabled="!canEdit" class="mt-1 w-4 h-4" />
                  <span>
                    <span class="text-sm font-medium text-gray-800">{{ d.label }}</span>
                    <span class="block text-xs text-gray-500">{{ d.description }}</span>
                  </span>
                </label>
              </div>
            </div>

            <!-- Capacités -->
            <div v-else-if="onglet === 'capacites'" class="space-y-6">
              <p class="text-sm text-gray-600 flex items-start gap-2">
                <Info class="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                Un rôle sans capacité n'a aucun de ces droits. <strong>Serveur</strong> : le serveur refuse l'action sans la capacité.
                <strong>Interface</strong> : ne fait que montrer ou cacher une page ou un bouton.
              </p>
              <div v-for="groupe in groupes" :key="groupe.categorie">
                <h3 class="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{{ groupe.categorie }}</h3>
                <div class="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  <div v-for="c in groupe.capacites" :key="c.key" class="flex flex-wrap items-center gap-3 px-3 py-2.5">
                    <input type="checkbox" :checked="c.key in brouillon.capacites" :disabled="!canEdit"
                      @change="basculerCapacite(c.key, ($event.target as HTMLInputElement).checked)" class="w-4 h-4" />
                    <div class="flex-1 min-w-[14rem]">
                      <div class="text-sm font-medium text-gray-800">{{ c.label }}</div>
                      <div class="text-xs text-gray-500">{{ c.description }}</div>
                    </div>
                    <span :class="['text-[11px] font-semibold px-2 py-0.5 rounded-full',
                      c.portee === 'serveur' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600']">
                      {{ c.portee === 'serveur' ? 'Serveur' : 'Interface' }}
                    </span>
                    <select v-if="c.key in brouillon.capacites" v-model="brouillon.capacites[c.key]" :disabled="!canEdit"
                      class="border border-gray-300 rounded-lg px-2 py-1 text-xs max-w-[16rem]">
                      <option v-for="o in catalogue.conditions" :key="o.value" :value="o.value">{{ o.label }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Circuit -->
            <div v-else-if="onglet === 'circuit'" class="space-y-4">
              <p v-if="brouillon.circuit_historique"
                class="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 flex gap-2">
                <AlertTriangle class="w-4 h-4 mt-0.5 shrink-0" />
                Le circuit de ce rôle est écrit dans le code : ce qui est saisi ici est ignoré. Seuls les rôles absents des circuits historiques (Agent, Chef de Service, Chef de Département, Directeur, Assistant SGA, SGA, CT) sont lus en base.
              </p>
              <p class="text-sm text-gray-600 flex items-start gap-2">
                <Info class="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                Étapes que traverse une note rédigée par ce rôle, dans l'ordre. Sans étape, la note va directement au SG.
              </p>
              <div v-if="brouillon.circuit.length" class="overflow-x-auto border border-gray-200 rounded-lg">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide">
                      <th class="px-3 py-2 w-16">Ordre</th>
                      <th class="px-3 py-2">Rôle qui valide</th>
                      <th class="px-3 py-2">Périmètre</th>
                      <th class="px-3 py-2 text-center">Paraphe</th>
                      <th class="px-3 py-2 text-center">Signature</th>
                      <th class="px-3 py-2">Libellé</th>
                      <th class="px-3 py-2 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(e, i) in brouillon.circuit" :key="i" class="border-t border-gray-100 align-middle">
                      <td class="px-3 py-2 font-semibold text-gray-500">{{ i + 1 }}</td>
                      <td class="px-3 py-2">
                        <select v-model.number="e.step_role_id" :disabled="!canEdit" class="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                          <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.nom }}</option>
                        </select>
                      </td>
                      <td class="px-3 py-2">
                        <select v-model="e.scope" :disabled="!canEdit" class="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                          <option v-for="s in catalogue.scopes" :key="s.value" :value="s.value">{{ s.label }}</option>
                        </select>
                      </td>
                      <td class="px-3 py-2 text-center"><input type="checkbox" v-model="e.requires_paraphe" :disabled="!canEdit" class="w-4 h-4" /></td>
                      <td class="px-3 py-2 text-center"><input type="checkbox" v-model="e.requires_signature" :disabled="!canEdit" class="w-4 h-4" /></td>
                      <td class="px-3 py-2"><input v-model="e.label" :disabled="!canEdit" maxlength="60" placeholder="(nom du rôle)" class="border border-gray-300 rounded-lg px-2 py-1 text-sm w-36" /></td>
                      <td class="px-3 py-2 text-right whitespace-nowrap">
                        <template v-if="canEdit">
                          <button @click="deplacer(i, -1)" :disabled="i === 0" class="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30" title="Monter"><ArrowUp class="w-4 h-4" /></button>
                          <button @click="deplacer(i, 1)" :disabled="i === brouillon.circuit.length - 1" class="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30" title="Descendre"><ArrowDown class="w-4 h-4" /></button>
                          <button @click="brouillon.circuit.splice(i, 1)" class="p-1 text-red-500 hover:text-red-700" title="Retirer"><Trash2 class="w-4 h-4" /></button>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="py-8 text-center text-sm text-gray-400 border border-dashed border-gray-300 rounded-lg">Aucune étape.</div>
              <div v-if="canEdit" class="flex flex-wrap gap-2">
                <button @click="ajouterEtape('self')" class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">+ Étape de l'auteur</button>
                <button @click="ajouterEtape('service')" class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">+ Étape du service</button>
                <button @click="ajouterEtape('national')" class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">+ Étape nationale</button>
              </div>
            </div>

            <!-- Aperçu -->
            <div v-else class="space-y-4">
              <p class="text-sm text-gray-600 flex items-start gap-2">
                <Eye class="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                Ce que verrait un titulaire de ce rôle, selon son rattachement. L'aperçu reflète le dernier enregistrement.
              </p>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div v-for="a in apercuCartes" :key="a.cle" class="border border-gray-200 rounded-lg p-4">
                  <h3 class="text-sm font-semibold text-gray-800 mb-2">{{ a.titre }}</h3>
                  <div class="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Menu</div>
                  <ul class="text-sm text-gray-700 mb-3 space-y-0.5">
                    <li v-for="p in a.pages" :key="p.key">· {{ p.label.replace('Page : ', '') }}</li>
                    <li v-if="!a.pages.length" class="text-gray-400">Aucune page</li>
                  </ul>
                  <div class="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">Droits</div>
                  <ul class="text-sm text-gray-700 space-y-0.5">
                    <li v-for="c in a.capacites" :key="c">· {{ libelle(c) }}</li>
                    <li v-if="!a.capacites.length" class="text-gray-400">Aucun droit particulier</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Barre d'enregistrement -->
          <div v-if="canEdit" class="px-5 py-3 border-t border-gray-100 flex flex-wrap items-center gap-3 bg-gray-50 rounded-b-xl">
            <button @click="enregistrer" :disabled="enregistrement || !modifie"
              class="px-5 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-40">
              {{ enregistrement ? 'Enregistrement…' : 'Enregistrer' }}
            </button>
            <span v-if="modifie" class="text-xs text-amber-700">Modifications non enregistrées</span>
            <span v-if="message" class="text-emerald-600 text-sm flex items-center gap-1"><Check class="w-4 h-4" /> {{ message }}</span>
            <span v-if="erreur" class="text-red-500 text-sm">{{ erreur }}</span>
          </div>
        </section>
        <section v-else class="bg-white rounded-xl border border-dashed border-gray-300 py-20 text-center text-gray-400">
          Choisissez un rôle dans la liste.
        </section>
      </div>

      <!-- ============ CRÉATION ============ -->
      <div v-if="creation" class="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" @click.self="creation = null">
        <form @submit.prevent="creer" class="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
          <h2 class="text-lg font-bold text-gray-900">Nouveau rôle</h2>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Code</label>
            <input v-model="creation.code" required placeholder="CHEF_DIVISION" maxlength="20"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase" />
            <p class="text-xs text-gray-400 mt-1">MAJUSCULES, chiffres, tirets bas. Définitif : il ne pourra plus être changé.</p>
          </div>
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Nom affiché</label>
            <input v-model="creation.nom" required maxlength="100" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Niveau</label>
              <input v-model.number="creation.niveau_hierarchique" required type="number" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Rang (circuits)</label>
              <input v-model.number="creation.rang_circuit" type="number" placeholder="(aucun)" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <p v-if="erreurCreation" class="text-sm text-red-500">{{ erreurCreation }}</p>
          <div class="flex justify-end gap-2 pt-2">
            <button type="button" @click="creation = null" class="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
            <button type="submit" class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90">Créer</button>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue'
import { KeyRound, Plus, Trash2, ArrowUp, ArrowDown, Check, Info, Eye, AlertTriangle } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'

interface Capacite { key: string; label: string; description: string; category: string; portee: 'serveur' | 'interface' }
interface Option { value: string; label: string }
interface Drapeau { key: string; label: string; description: string }
interface Catalogue { capacites: Capacite[]; conditions: Option[]; scopes: Option[]; drapeaux: Drapeau[] }
interface Resume {
  id: number; code: string; nom: string; niveau_hierarchique: number; rang_circuit: number | null
  nb_utilisateurs: number; nb_capacites: number; nb_etapes_circuit: number
}
interface Etape {
  ordre?: number; step_role_id: number; scope: string
  requires_paraphe: boolean; requires_signature: boolean; label: string
}
interface Apercu { pages: { key: string; label: string }[]; capacites: string[] }
interface Fiche extends Resume {
  description: string
  circuit: Etape[]
  capacites: { capability: string; condition: string }[]
  circuit_historique: boolean
  apercu: Record<string, Apercu>
  can_edit: boolean
  [drapeau: string]: unknown
}
interface Brouillon {
  code: string; nom: string; niveau_hierarchique: number; rang_circuit: number | null; description: string
  drapeaux: Record<string, boolean>
  capacites: Record<string, string>
  circuit: Etape[]
  circuit_historique: boolean
}

const onglets = [
  { id: 'general', label: 'Général' },
  { id: 'capacites', label: 'Capacités' },
  { id: 'circuit', label: 'Circuit' },
  { id: 'apercu', label: 'Aperçu' },
]

const catalogue = reactive<Catalogue>({ capacites: [], conditions: [], scopes: [], drapeaux: [] })
const roles = ref<Resume[]>([])
const canEdit = ref(false)
const chargement = ref(false)
const erreurChargement = ref<string | null>(null)
const recherche = ref('')
const selectionId = ref<number | null>(null)
const fiche = ref<Fiche | null>(null)
const brouillon = ref<Brouillon | null>(null)
const instantane = ref('')
const onglet = ref('general')
const enregistrement = ref(false)
const message = ref<string | null>(null)
const erreur = ref<string | null>(null)
const creation = ref<{ code: string; nom: string; niveau_hierarchique: number | null; rang_circuit: number | null } | null>(null)
const erreurCreation = ref<string | null>(null)

const rolesFiltres = computed(() => {
  const q = recherche.value.trim().toLowerCase()
  return q ? roles.value.filter((r) => r.nom.toLowerCase().includes(q) || r.code.toLowerCase().includes(q)) : roles.value
})

const groupes = computed(() => {
  const parCategorie = new Map<string, Capacite[]>()
  for (const c of catalogue.capacites) {
    if (!parCategorie.has(c.category)) parCategorie.set(c.category, [])
    parCategorie.get(c.category)!.push(c)
  }
  return Array.from(parCategorie.entries()).map(([categorie, capacites]) => ({ categorie, capacites }))
})

const apercuCartes = computed(() => {
  const a = fiche.value?.apercu
  if (!a) return []
  return [
    { cle: 'sans_rattachement', titre: 'Sans rattachement', ...a.sans_rattachement },
    { cle: 'avec_direction', titre: 'Rattaché à une direction', ...a.avec_direction },
    { cle: 'avec_ambassade', titre: 'Rattaché à une ambassade', ...a.avec_ambassade },
  ]
})

const libelle = (cle: string) => catalogue.capacites.find((c) => c.key === cle)?.label ?? cle

// Ce qui part au serveur : sert aussi à savoir si quelque chose a changé.
const charge = (b: Brouillon) => ({
  nom: b.nom, niveau_hierarchique: b.niveau_hierarchique, rang_circuit: b.rang_circuit ?? null,
  description: b.description, ...b.drapeaux,
  capacites: Object.entries(b.capacites).map(([capability, condition]) => ({ capability, condition })),
  circuit: b.circuit.map((e, i) => ({
    ordre: i + 1, step_role_id: e.step_role_id, scope: e.scope,
    requires_paraphe: e.requires_paraphe, requires_signature: e.requires_signature, label: e.label || '',
  })),
})
const modifie = computed(() => !!brouillon.value && JSON.stringify(charge(brouillon.value)) !== instantane.value)

function depuisFiche(f: Fiche): Brouillon {
  return {
    code: f.code, nom: f.nom, niveau_hierarchique: f.niveau_hierarchique, rang_circuit: f.rang_circuit,
    description: f.description || '',
    drapeaux: Object.fromEntries(catalogue.drapeaux.map((d) => [d.key, Boolean(f[d.key])])),
    capacites: Object.fromEntries(f.capacites.map((c) => [c.capability, c.condition || ''])),
    circuit: f.circuit.map((e) => ({ ...e })),
    circuit_historique: f.circuit_historique,
  }
}

function poser(f: Fiche) {
  fiche.value = f
  brouillon.value = depuisFiche(f)
  instantane.value = JSON.stringify(charge(brouillon.value))
}

async function chargerListe() {
  const data = await apiFetch<{ roles: Resume[]; can_edit: boolean }>('/api/admin/role-engine/roles/')
  roles.value = data.roles
  canEdit.value = data.can_edit
}

async function choisir(id: number) {
  if (id === selectionId.value) return
  if (modifie.value && !window.confirm('Des modifications ne sont pas enregistrées. Les abandonner ?')) return
  message.value = erreur.value = null
  selectionId.value = id
  try {
    poser(await apiFetch<Fiche>(`/api/admin/role-engine/roles/${id}/`))
  } catch (e) {
    erreur.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  }
}

function basculerCapacite(cle: string, accordee: boolean) {
  if (!brouillon.value) return
  if (accordee) brouillon.value.capacites[cle] = ''
  else delete brouillon.value.capacites[cle]
}

function ajouterEtape(scope: string) {
  if (!brouillon.value || !fiche.value) return
  const parDefaut = scope === 'self' ? fiche.value.id : roles.value.find((r) => r.code === (scope === 'national' ? 'SG' : 'CHEF_SERVICE'))?.id ?? fiche.value.id
  brouillon.value.circuit.push({ step_role_id: parDefaut, scope, requires_paraphe: true, requires_signature: false, label: '' })
}

function deplacer(i: number, sens: number) {
  const c = brouillon.value?.circuit
  if (!c) return
  const [e] = c.splice(i, 1)
  c.splice(i + sens, 0, e)
}

async function enregistrer() {
  if (!brouillon.value || selectionId.value === null) return
  enregistrement.value = true
  message.value = erreur.value = null
  try {
    const f = await apiFetch<Fiche>(`/api/admin/role-engine/roles/${selectionId.value}/`, {
      method: 'PUT', body: JSON.stringify(charge(brouillon.value)),
    })
    poser(f)
    await chargerListe()
    message.value = 'Enregistré'
    setTimeout(() => { message.value = null }, 3000)
  } catch (e) {
    erreur.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    enregistrement.value = false
  }
}

function ouvrirCreation() {
  erreurCreation.value = null
  creation.value = { code: '', nom: '', niveau_hierarchique: null, rang_circuit: null }
}

async function creer() {
  if (!creation.value) return
  erreurCreation.value = null
  try {
    const f = await apiFetch<Fiche>('/api/admin/role-engine/roles/', {
      method: 'POST',
      body: JSON.stringify({ ...creation.value, code: creation.value.code.trim().toUpperCase() }),
    })
    creation.value = null
    await chargerListe()
    selectionId.value = f.id
    poser(f)
    onglet.value = 'capacites'
  } catch (e) {
    erreurCreation.value = e instanceof Error ? e.message : 'Échec de la création.'
  }
}

onMounted(async () => {
  chargement.value = true
  try {
    Object.assign(catalogue, await apiFetch<Catalogue>('/api/admin/role-engine/catalogue/'))
    await chargerListe()
  } catch (e) {
    erreurChargement.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    chargement.value = false
  }
})
</script>
